/**
 * ============================================================
 * KEJJA OS — src/lib/api.ts
 * Typed fetch wrapper for our backend API.
 * ============================================================
 *
 * Uses:
 *   - relative URLs so Vite proxy (dev) and Nginx (prod) both work
 *   - credentials: 'include' so cookies are sent/received
 *   - auto-throws on non-2xx with a typed error shape
 * ============================================================
 */

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

// ------------------------------------------------------------
// Error type
// ------------------------------------------------------------

export class ApiError extends Error {
  public status: number;
  public code: string;
  public details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// ------------------------------------------------------------
// Core fetch wrapper
// ------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const init: RequestInit = {
    method,
    credentials: 'include',          // always send cookies
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, init);

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    // Non-JSON response
    if (!res.ok) {
      throw new ApiError(res.status, 'bad_response', `HTTP ${res.status}`);
    }
    return undefined as T;
  }

  if (!res.ok) {
    const errObj = (payload as Record<string, unknown>) ?? {};
    throw new ApiError(
      res.status,
      (errObj.error as string) ?? 'unknown_error',
      (errObj.message as string) ?? `HTTP ${res.status}`,
      errObj.details,
    );
  }

  return payload as T;
}

// ------------------------------------------------------------
// Convenience methods
// ------------------------------------------------------------

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

// ------------------------------------------------------------
// Typed domain calls (grow as we build features)
// ------------------------------------------------------------

export interface KejjaUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  phoneNumber: string | null;
  emailVerified: boolean;
  preferredLanguage: string;
  timezone: string;
  lastLoginAt: string | null;
  onboardedAt: string | null;
  createdAt?: string;
}

export interface KejjaHome {
  id: string;
  name: string;
  slug: string;
  motto: string | null;
  country: string;
  region: string | null;
  currencyCode: string;
  timezone: string;
  createdByUserId: string | null;
  settingsJson: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface KejjaCommunity {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  communityType: string;
  isInviteOnly: boolean;
  joinCode: string | null;
  maxHomes: number;
  createdByUserId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Auth
export const authApi = {
  signInWithFirebase: (idToken: string) =>
    api.post<{ ok: true; user: KejjaUser }>('/auth/session', { idToken }),

  me: () => api.get<KejjaUser>('/auth/me'),

  logout: () => api.post<{ ok: true }>('/auth/logout'),
};

// "Me" aggregations
export const meApi = {
  profile: () => api.get<KejjaUser>('/me'),

  homes: () =>
    api.get<{ homes: Array<{ home: KejjaHome; role: string; displayNickname: string | null }> }>(
      '/me/homes',
    ),

  communities: () =>
    api.get<{ communities: Array<{ community: KejjaCommunity; homeId: string; role: string }> }>(
      '/me/communities',
    ),

  invitations: () =>
    api.get<{
      invitations: Array<{
        id: string;
        token: string;
        kind: 'home' | 'community';
        targetId: string;
        role: string | null;
        message: string | null;
        expiresAt: string;
        invitedByUserId: string | null;
        createdAt: string;
      }>;
    }>('/me/invitations'),

  updateProfile: (patch: {
    displayName?: string;
    avatarUrl?: string | null;
    phoneNumber?: string | null;
    preferredLanguage?: string;
    timezone?: string;
  }) => api.patch<KejjaUser>('/me', patch),
};
